
import { Vue, Options, prop } from "vue-class-component";
import OverlayPanel from "primevue/overlaypanel";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { DestinationCity } from "@/domain/entities/CreateBookingV3";
import { DetailClientManagement } from "@/domain/entities/ClientManagement";
import Checkbox from "primevue/checkbox";
import { ConfigPriceWoodpackingController } from "@/app/ui/controllers/ConfigPriceWoodpackingController";
import { WoodpackingData } from "@/domain/entities/ConfigWoodpacking";
import { formatPriceRP } from "@/app/infrastructures/misc/Utils";
import Skeleton from "@/app/ui/components/skeleton/index.vue";
import { parseZodError } from "../../utils";
import { deliveryServiceValidationScheme } from "./validation";
import { ResponsePayloadV2 } from "@/domain/entities/ResponsePayload";
import { CodConfigurationController } from "@/app/ui/controllers/CodConfigurationController";
import { ApiRequestList } from "@/domain/entities/MainApp";
import { SttAdjustmentRule } from "@/domain/entities/ShipmentBooking";
import absolutePosition from "@/app/infrastructures/misc/common-library/dom/absolutePosition";
import { FlagsMedusa } from "@/feature-flags/flags-misc-medusa";
import WoodpackingCheckbox from "@/app/ui/views/shipment/booking/component/woodpacking-checkbox/index.vue";
import { debounce } from "lodash";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { BookingController } from "@/app/ui/controllers/BookingController";

class Props {
  modelValue = prop<string>({
    default: "",
    required: true
  });
  woodpacking = prop<boolean>({
    default: false,
    required: true
  });
  destination = prop<DestinationCity>({
    required: true,
    default: () => new DestinationCity()
  });
  formType = prop<"manual" | "client">({
    required: true,
    default: "manual"
  });
  clientManagementDetail = prop<DetailClientManagement>({
    required: true,
    default: () => new DetailClientManagement()
  });
  // for woorpacking
  originCityCode = prop<string>({
    default: "",
    required: true
  });
  // for cod fraud
  isLimited = prop<boolean>({
    default: false,
    required: true
  });
  // for cod fraud
  isBlacklist = prop<boolean>({
    default: false,
    required: true
  });
  disabled = prop<boolean>({
    default: false,
    required: false
  });
  disabledCOD = prop<boolean>({
    default: false,
    required: false
  });
  disabledDFOD = prop<boolean>({
    default: false,
    required: false
  });
  serviceTypeIndex = prop<number>({
    required: true
  });
  currentIndex = prop<number>({
    required: true
  });
  configDeliveryService = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  isEdit = prop<boolean>({
    default: false,
    type: Boolean
  });
  hideWoodpacking = prop<boolean>({
    default: false,
    type: Boolean
  });
  idNonCod = prop<string>({
    default: "",
    type: String
  });
  idCod = prop<string>({
    default: "",
    type: String
  });
  idDfod = prop<string>({
    default: "",
    type: String
  });
  isClient = prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  components: {
    OverlayPanel,
    Checkbox,
    Skeleton
  },
  watch: {
    isDisabledCODButton(val: boolean) {
      if (this.isEdit) return;
      if (val && this.deliveryServiceModel === "cod")
        this.$emit("update:modelValue", "non-cod");
    },
    isDisabledDFODButton(val: boolean) {
      if (this.isEdit) return;
      if (val && this.deliveryServiceModel === "dfod")
        this.$emit("update:modelValue", "non-cod");
    },
    isAllowWoodpacking(val: boolean) {
      if (!val && this.woodpackingModel)
        this.$emit("update:woodpacking", false);
    },
    deliveryServiceModel: {
      handler(val: string) {
        parseZodError(deliveryServiceValidationScheme, val);
      },
      immediate: true
    }
  }
})
export default class DeliveryService extends Vue.with(Props) {
  woodpackingData = new WoodpackingData();
  CODConfigurationData = new ResponsePayloadV2();
  onHoverDeliveryService = "cod";

  mounted() {
    this.getWoodpackingDetail();
  }

  get deliveryServiceModel() {
    return this.modelValue;
  }

  set deliveryServiceModel(val: string) {
    this.$emit("update:modelValue", val);
  }

  get woodpackingModel() {
    return this.woodpacking;
  }

  set woodpackingModel(val: boolean) {
    this.$emit("update:woodpacking", val);
  }

  get tooltipStyle() {
    return {
      fontFamily: "Montserrat",
      width: "400px",
      backgroundColor: "#4D4D4D",
      color: "#FFFFFF",
      fontSize: "12px",
      marginTop: "20px",
      visibility: "hidden"
    };
  }

  get isRetailBooking() {
    return this.formType === "manual";
  }

  get isDisabledCOD() {
    if ([!this.isEligiblePartnerCOD, !this.isEligibleClientCOD].includes(true))
      return true;
    if ([this.isPhoneNumberLimit, this.isPhoneNumberBlacklist].includes(true))
      return true;
    if (!this.destination.id) return false;
    return !this.isEligibleDestinationCOD;
  }

  get isDisabledDFOD() {
    if (this.disabledDFOD) return true;
    if (!this.isEligibleClientDFOD) return true;
    if (!this.destination.id) return false;
    return this.isDisabledCOD;
  }

  get isEligibleDestinationCOD() {
    if (!this.destination.id) return true;
    // District isCod is the single source of truth for both domestic and
    // cross-country routes — a foreign district already reflects COD
    // Internasional eligibility once the backend config is active for it.
    return this.destination.isCod === "yes";
  }

  get isEligiblePartnerCOD() {
    if (this.isForeignAccount && !this.isClient) return false;
    const isPartnerCODBooking =
      AccountController.accountData.account_type_detail.partnerIsCodBooking;
    if (!this.isPOSAccount) return true;
    return this.isPOSAccount && isPartnerCODBooking;
  }

  get isEligibleClientCOD() {
    if (this.isPOSAccount && this.isRetailBooking) return true;
    if (this.isNonClientAccount && !this.clientManagementDetail.id) return true;
    if (this.isNonClientAccount) return this.clientManagementDetail.clientIsCod;
    return AccountController.accountData.account_type_detail.isCod;
  }

  get isEligibleClientDFOD() {
    if (this.isPOSAccount && this.isRetailBooking) return true;
    if (this.isNonClientAccount && !this.clientManagementDetail.id) return true;
    if (this.isNonClientAccount)
      return this.clientManagementDetail.clientIsDfod;
    return AccountController.accountData.account_type_detail.isDfod;
  }

  get isPhoneNumberBlacklist() {
    return this.isBlacklist;
  }

  get isPhoneNumberLimit() {
    return this.isLimited;
  }

  get isDisabledCODButton() {
    return [this.disabled, this.disabledCOD, this.isDisabledCOD].includes(true);
  }

  get isDisabledDFODButton() {
    return [
      this.disabled,
      this.disabledCOD,
      this.disabledDFOD,
      this.isDisabledCOD,
      this.isDisabledDFOD
    ].includes(true);
  }

  get isForeignAccount() {
    return AccountController.accountData.accountIsForeign;
  }

  get disabledCODMessage() {
    if (this.isPhoneNumberBlacklist) {
      return this.$t("bookingV3.deliveryService.error.phoneNumberBlacklist");
    }
    if (this.isPhoneNumberLimit) {
      return this.$t("bookingV3.deliveryService.error.phoneNumberLimit");
    }
    if (!this.isEligiblePartnerCOD || !this.isEligibleClientCOD) {
      return this.$t("bookingV3.deliveryService.error.eligiblePartnerCOD");
    }
    if (!this.isEligibleDestinationCOD) {
      return this.$t("bookingV3.deliveryService.error.eligibleDestinationCOD");
    }
    return this.$t("bookingV3.deliveryService.error.eligibleCOD");
  }

  get isShowWoodpacking() {
    return !this.hideWoodpacking && (
      !this.isRetailBooking ||
      AccountController.accountData.isAllClient ||
      AccountController.accountData.isInternalAccount ||
      this.isEdit
    );
  }

  get isAllowWoodpacking() {
    if (!this.originCityCode) return true;
    if (!this.woodpackingData.woodpackingAvailableCities.length) return false;
    if (this.woodpackingData.woodpackingAvailableCities[0].cityCode === "ALL")
      return true;
    return this.woodpackingData.woodpackingAvailableCities
      .map((city) => city.cityCode)
      .includes(this.originCityCode);
  }

  get additionalWoodpackingPrice() {
    return formatPriceRP(
      this.woodpackingData.additionalCost,
      AccountController.accountData.accountCurrency
    );
  }

  get isLoadingWoodpacking() {
    return ConfigPriceWoodpackingController.isLoading;
  }

  get CODTooltipContent() {
    return {
      title: "Cash On Delivery",
      content: [
        "<strong>Pengirim</strong> tidak perlu membayar ongkir.",
        "Pengiriman sudah termasuk proteksi.",
        "Harga barang COD minimal Rp10.000 - Rp5.000.000. Jika paket sudah sampai, <strong>pengirim</strong> dikenakan biaya COD minimal Rp4.000 atau 4% (Mana yang lebih besar) dari total (harga barang + ongkir). Jika terjadi retur, <strong>pengirim</strong> membayar 50% dari total biaya ongkir.",
        "<strong>Pengirim</strong> akan menerima penghasilan COD di saldo aplikasi Lion Parcel.",
        "Jika paket sudah sampai, <strong>penerima</strong> hanya membayar ongkir dan harga barang.",
        "<strong>POS booking</strong> mendapatkan diskon Agen sebesar 1,5% apabila paket diterima. Jika pengiriman gagal, tidak akan dapat diskon."
      ]
    };
  }

  get DFODTooltipContent() {
    return {
      title: "Delivery Fee On Delivery",
      content: [
        "<strong>Pengirim</strong> tidak perlu membayar ongkir.",
        "Pengiriman belum termasuk proteksi.",
        "Jika paket sudah sampai, <strong>penerima</strong> hanya membayar ongkir, dengan tambahan biaya DFOD minimal Rp1.000 atau 3% (Mana yang besar) dari total biaya ongkir. Jika terjadi retur, <strong>pengirim</strong> membayar 50% dari total biaya ongkir.",
        "<strong>Pengirim</strong> akan menerima penghasilan COD di saldo aplikasi Lion Parcel.",
        "<strong>POS booking</strong> mendapatkan diskon Agen sebesar 1% apabila paket diterima. Jika pengiriman gagal, tidak akan dapat diskon."
      ]
    };
  }

  get deliveryTooltipContent() {
    if (this.onHoverDeliveryService === "cod") {
      return this.CODTooltipContent;
    }

    return this.DFODTooltipContent;
  }

  get isPOSAccount() {
    const isPOSAccount = AccountController.accountData.isPosAccount;
    return isPOSAccount;
  }

  get isNonClientAccount() {
    const isInternalAccount = AccountController.accountData.isInternalAccount;

    return AccountController.accountData.isAllPartner || isInternalAccount;
  }

  checkSelectedDisabledEdit(type = "non-cod") {
    return this.isEdit && this.modelValue === type;
  }

  onChangeDeliveryService(val: string) {
    this.deliveryServiceModel = val;
    if (val === "cod" || val === "dfod") this.getCODConfiguration();
  }

  async getCODConfiguration() {
    this.CODConfigurationData = await CodConfigurationController.getList(
      new ApiRequestList({
        page: this.CODConfigurationData.pagination.page,
        limit: this.CODConfigurationData.pagination.limit
      })
    );

    this.$emit("cod-config", this.CODConfigurationData);
  }

  async getWoodpackingDetail() {
    this.woodpackingData = await ConfigPriceWoodpackingController.getDetailWoopacking(
      { id: 3 }
    );
  }

  async hideTooltip(event: any, service: string) {
    this.onHoverDeliveryService = service;
    const refs: any = this.$refs;
    refs["delivery-service-tooltip"].hide(event);
  }

  async toggleTooltip(event: any, service: string) {
    if (
      service === "cod" &&
      [this.isDisabledCOD, this.disabled, this.disabledCOD].includes(true)
    )
      return;
    if (
      service === "dfod" &&
      [
        this.isDisabledDFOD,
        this.disabled,
        this.disabledCOD,
        this.disabledCOD
      ].includes(true)
    )
      return;
    this.onHoverDeliveryService = service;
    const refs: any = this.$refs;
    refs["delivery-service-tooltip"].show(event);
    await this.$nextTick();
    const container = document.getElementById(
      "delivery-service-tooltip-container"
    );
    if (!container) return;
    absolutePosition(
      container,
      event.currentTarget,
      refs["delivery-service-tooltip"].visible,
      "center"
    );
  }

  changeFieldIndex() {
    this.$emit("update:currentIndex", this.serviceTypeIndex);
  }
}
